import React, { useRef } from 'react';
import { View } from 'react-native';
import MyAppText from './MyAppText';
import { TextInputMask, TextInputMaskMethods } from 'react-native-masked-text';
import { translate } from '../services/translate';
import { useStyles } from 'react-native-unistyles';
import moment from 'moment';
import inputStylesheet from '../styles/inputStyles';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';

type MyDateInputProps = {
    label: string;
    editable?: boolean;
    hasMaxWidth?: boolean;
    labelFontSize?: number;
    required?: boolean;
    allowDateBiggerThanToday?: boolean;
} & ({
    date: string;
    onChangeDate: (value: string) => void;
    isInvalid: boolean;
    setIsInvalid: (value: boolean) => void;
    form?: never;
    fieldName?: never;
} | {
    date?: never;
    onChangeDate?: never;
    isInvalid?: never;
    setIsInvalid?: never;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
});

type InputMask = TextInputMask & TextInputMaskMethods;

export default function MyDateInput({
    form,
    fieldName,
    label,
    date,
    onChangeDate,
    hasMaxWidth = false,
    editable = true,
    required = false,
    allowDateBiggerThanToday = true,
    isInvalid,
    setIsInvalid,
}: MyDateInputProps) {
    const { styles: defaultInputStyle } = useStyles(inputStylesheet);

    const dateInputRef = useRef<InputMask>(null);

    function isDateValid(date: string) {
        if (!date && !required) {
            return true;
        }
        const formattedDate = moment(date, 'DD/MM/YYYY', true);

        return formattedDate.isValid();
    }

    function isDateBiggerThanToday(date: string) {
        if (!date && !required) {
            return true;
        }

        const formattedDate = moment(date, 'DD/MM/YYYY', true);

        if (!formattedDate.isValid()) {
            return false;
        }

        if (!allowDateBiggerThanToday && formattedDate.isAfter(moment())) {
            return false;
        }

        return true;

    }

    if (form && fieldName) {
        const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

        if (required) {
            rules.required = translate('required_field');
        }

        rules.validate = {
            validateDate: (value) => isDateValid(value) || translate('invalidDate')
        };

        if (!allowDateBiggerThanToday) {
            rules.validate.dateBiggerThanToday = (value) => isDateBiggerThanToday(value) || translate('invalidDate');
        }

        return <Controller
            control={form.control}
            name={fieldName}
            rules={rules}
            render={({ field, fieldState }) => (
                <View style={defaultInputStyle.container}>
                    <View style={defaultInputStyle.labelContainer}>
                        {label && <MyAppText style={defaultInputStyle.label}>{label}</MyAppText>}
                        {required && label ?
                            <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                            : null}
                    </View>
                    <TextInputMask
                        value={field.value}
                        placeholder='__/__/____'
                        onChangeText={field.onChange}
                        keyboardType={'numeric'}
                        type={'datetime'}
                        style={[defaultInputStyle.input({
                            invalid: Boolean(fieldState.error),
                            disabled: !editable,
                            placeholder: !field.value,
                        }), hasMaxWidth ? { maxWidth: 125 } : {}]}
                        options={{ format: 'DD/MM/YYYY' }}
                        onBlur={field.onBlur}
                        ref={dateInputRef}
                        editable={editable}
                    />
                    {isInvalid
                        ? <MyAppText style={defaultInputStyle.errorMessage}>{translate('invalidDate')}</MyAppText>
                        : fieldState.error ? <MyAppText style={defaultInputStyle.errorMessage}>{fieldState.error.message}</MyAppText> : null}
                </View>
            )}
        />;
    } else if (date !== undefined && onChangeDate !== undefined) {

        return (
            <View style={defaultInputStyle.container}>
                <View style={defaultInputStyle.labelContainer}>
                    {label && <MyAppText style={defaultInputStyle.label}>{label}</MyAppText>}
                    {required && label ?
                        <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                        : null}
                </View>
                <TextInputMask
                    value={date}
                    placeholder='__/__/____'
                    onChangeText={(text: string) => {
                        onChangeDate(text);
                    }}
                    keyboardType={'numeric'}
                    type={'datetime'}
                    style={[defaultInputStyle.input({
                        invalid: Boolean(isInvalid),
                        disabled: !editable,
                        placeholder: !date,
                    }), hasMaxWidth ? { maxWidth: 125 } : {}]}
                    options={{
                        format: 'DD/MM/YYYY'
                    }}
                    onBlur={() => {
                        if (!date && !required) {
                            return setIsInvalid(false);
                        }

                        const formattedDate = moment(date, 'DD/MM/YYYY', true);

                        if (!formattedDate.isValid()) {
                            return setIsInvalid(true);
                        }

                        if (!allowDateBiggerThanToday && formattedDate.isAfter(moment())) {
                            return setIsInvalid(true);
                        }

                        return setIsInvalid(false);
                    }}
                    ref={dateInputRef}
                    editable={editable}
                />
                {isInvalid ?
                    <MyAppText style={defaultInputStyle.errorMessage}>{translate('invalidDate')}</MyAppText> : null}
            </View>
        );
    }
}
